import React, { useMemo } from 'react';
import { Table, Tabs } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type {
  ARMonthlyRow,
  DeptCompanySummary,
  DeptSummary,
  CompanySummary,
  CurrencyTotal,
  QueryResponse,
} from '../../../services/g303Api';

interface ResultTableProps {
  result: QueryResponse;
}

const fmtNum = (v: number): string => (v ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const detailColumns: ColumnsType<ARMonthlyRow & { _key: string }> = [
  {
    title: '部門',
    dataIndex: 'deptCode',
    key: 'deptCode',
    width: 60,
    render: (code: string, record) =>
      `${(code || '').trim()} ${(record.deptName || '').trim()}`,
  },
  {
    title: '公司',
    dataIndex: 'company',
    key: 'company',
    width: 100,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '業務',
    dataIndex: 'salesCode',
    key: 'salesCode',
    width: 80,
    render: (code: string, record) =>
      `${(code || '').trim()} ${(record.salesName || '').trim()}`,
  },
  {
    title: '年月',
    dataIndex: 'yearMonth',
    key: 'yearMonth',
    width: 80,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '幣別',
    dataIndex: 'currency',
    key: 'currency',
    width: 50,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '數量',
    dataIndex: 'amount1',
    key: 'amount1',
    width: 100,
    align: 'right' as const,
    render: (v: number) => fmtNum(v),
  },
  {
    title: '金額',
    dataIndex: 'amount2',
    key: 'amount2',
    width: 120,
    align: 'right' as const,
    render: (v: number) => fmtNum(v),
  },
];

const deptCompanySumColumns: ColumnsType<DeptCompanySummary & { _key: string }> = [
  { title: '部門', dataIndex: 'deptCode', key: 'deptCode', width: 60,
    render: (code: string, record) => `${(code || '').trim()} ${(record.deptName || '').trim()}` },
  { title: '公司', dataIndex: 'company', key: 'company', width: 100, render: (v: string) => (v || '').trim() },
  { title: '幣別', dataIndex: 'currency', key: 'currency', width: 50 },
  { title: '數量', dataIndex: 'amount1', key: 'amount1', width: 100, align: 'right' as const, render: (v: number) => fmtNum(v) },
  { title: '金額', dataIndex: 'amount2', key: 'amount2', width: 120, align: 'right' as const, render: (v: number) => fmtNum(v) },
];

const deptSumColumns: ColumnsType<DeptSummary & { _key: string }> = [
  { title: '部門', dataIndex: 'deptCode', key: 'deptCode', width: 60,
    render: (code: string, record) => `${(code || '').trim()} ${(record.deptName || '').trim()}` },
  { title: '幣別', dataIndex: 'currency', key: 'currency', width: 50 },
  { title: '數量', dataIndex: 'amount1', key: 'amount1', width: 100, align: 'right' as const, render: (v: number) => fmtNum(v) },
  { title: '金額', dataIndex: 'amount2', key: 'amount2', width: 120, align: 'right' as const, render: (v: number) => fmtNum(v) },
];

const companySumColumns: ColumnsType<CompanySummary & { _key: string }> = [
  { title: '公司', dataIndex: 'company', key: 'company', width: 100, render: (v: string) => (v || '').trim() },
  { title: '幣別', dataIndex: 'currency', key: 'currency', width: 50 },
  { title: '數量', dataIndex: 'amount1', key: 'amount1', width: 100, align: 'right' as const, render: (v: number) => fmtNum(v) },
  { title: '金額', dataIndex: 'amount2', key: 'amount2', width: 120, align: 'right' as const, render: (v: number) => fmtNum(v) },
];

const currencyTotalColumns: ColumnsType<CurrencyTotal & { _key: string }> = [
  { title: '幣別', dataIndex: 'currency', key: 'currency', width: 50 },
  { title: '數量', dataIndex: 'amount1', key: 'amount1', width: 100, align: 'right' as const, render: (v: number) => fmtNum(v) },
  { title: '金額', dataIndex: 'amount2', key: 'amount2', width: 120, align: 'right' as const, render: (v: number) => fmtNum(v) },
];

const ResultTable: React.FC<ResultTableProps> = ({ result }) => {
  const detailData = useMemo(
    () => (result.details || []).map((row, i) => ({
      ...row,
      _key: `${row.deptCode}-${row.company}-${row.salesCode}-${row.yearMonth}-${row.currency}-${i}`,
    })),
    [result.details]
  );

  const deptCompanyData = useMemo(
    () => (result.deptCompanySummary || []).map((row, i) => ({
      ...row,
      _key: `${row.deptCode}-${row.company}-${row.currency}-${i}`,
    })),
    [result.deptCompanySummary]
  );

  const deptData = useMemo(
    () => (result.deptSummary || []).map((row, i) => ({
      ...row,
      _key: `${row.deptCode}-${row.currency}-${i}`,
    })),
    [result.deptSummary]
  );

  const companyData = useMemo(
    () => (result.companySummary || []).map((row, i) => ({
      ...row,
      _key: `${row.company}-${row.currency}-${i}`,
    })),
    [result.companySummary]
  );

  const totalData = useMemo(
    () => (result.currencyTotals || []).map((row, i) => ({
      ...row,
      _key: `${row.currency}-${i}`,
    })),
    [result.currencyTotals]
  );

  const paginationConfig = { pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] };

  const items = [
    {
      key: 'details',
      label: `明細 (${result.totalCount})`,
      children: (
        <Table columns={detailColumns} dataSource={detailData} rowKey="_key" size="small"
          scroll={{ x: 800, y: 500 }} pagination={paginationConfig} />
      ),
    },
  ];

  if (deptCompanyData.length > 0) {
    items.push({
      key: 'deptCompanySummary',
      label: `部門公司小計 (${deptCompanyData.length})`,
      children: (
        <Table columns={deptCompanySumColumns} dataSource={deptCompanyData} rowKey="_key" size="small"
          scroll={{ x: 500, y: 500 }} pagination={paginationConfig} />
      ),
    });
  }

  if (deptData.length > 0) {
    items.push({
      key: 'deptSummary',
      label: `部門小計 (${deptData.length})`,
      children: (
        <Table columns={deptSumColumns} dataSource={deptData} rowKey="_key" size="small"
          scroll={{ x: 400, y: 500 }} pagination={paginationConfig} />
      ),
    });
  }

  if (companyData.length > 0) {
    items.push({
      key: 'companySummary',
      label: `公司小計 (${companyData.length})`,
      children: (
        <Table columns={companySumColumns} dataSource={companyData} rowKey="_key" size="small"
          scroll={{ x: 400, y: 500 }} pagination={paginationConfig} />
      ),
    });
  }

  if (totalData.length > 0) {
    items.push({
      key: 'totals',
      label: `幣別總計 (${totalData.length})`,
      children: (
        <Table columns={currencyTotalColumns} dataSource={totalData} rowKey="_key" size="small"
          scroll={{ x: 300, y: 300 }} pagination={false} />
      ),
    });
  }

  return <Tabs items={items} />;
};

export default ResultTable;
